import { useEffect, useState } from 'react';
import Dialog, {
  DefaultDialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/Dialog';
import { DecoratedCompanyInvestor } from '@flemingmetrics/api-client';
import { useCompany, useDispatch, useInvestor } from '@/hooks/useStore';
import { addCompanyInvestor as dispatchAddCompanyInvestor } from '@/store/slices/companyInvestorsSlice';
import { addFinancialReport as dispatchAddFinancialReport } from '@/store/slices/financialReportsSlice';
import { useToggle } from '@/hooks/useToggle';
import { useAppState } from '@/hooks/useAppState';
import SearchableDropdown from '@/components/SearchableDropdown';
import { searchCompanies, searchInvestors } from '@/utils/filters';
import { showErrorToaster, showSuccessToaster } from '@/utils/toasters';
import { isEmpty } from '@/utils/misc';
import {
  addInvestorToCompany,
  buildCompanyOption,
  buildInvestorOption,
  classes,
  type SelectOption,
} from './utils';

export type NewCompanyInvestorDialogProps = {
  investorId?: number;
  companyId?: number;
  onSave?: (companyInvestor: DecoratedCompanyInvestor) => void;
  onClose: () => void;
};

export default function NewCompanyInvestorDialog({
  investorId,
  companyId,
  onSave,
  onClose,
}: NewCompanyInvestorDialogProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const [selectedInvestor, setSelectedInvestor] = useState<SelectOption | null>(
    null
  );
  const [selectedCompany, setSelectedCompany] = useState<SelectOption | null>(
    null
  );
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [isSaving, toggleSaving] = useToggle(false);
  const initialInvestor = useInvestor(investorId);
  const initialCompany = useCompany(companyId);

  useEffect(() => {
    if (initialInvestor) {
      setSelectedInvestor(buildInvestorOption(initialInvestor));
    }
  }, [initialInvestor]);

  useEffect(() => {
    if (initialCompany) {
      setSelectedCompany(buildCompanyOption(initialCompany));
    }
  }, [initialCompany]);

  const handleSave = async () => {
    try {
      let invalid: string[] = [];
      if (!selectedCompany) invalidFields.push('selectedCompany');
      if (!selectedInvestor) invalidFields.push('selectedInvestor');
      if (!isEmpty(invalid)) {
        showErrorToaster('Please enter a valid value for all fields');
        return setInvalidFields(invalid);
      }
      toggleSaving(true);
      setInvalidFields([]);
      const result = await addInvestorToCompany(
        selectedCompany!.value,
        selectedInvestor!.value
      );
      showSuccessToaster('Investor added to company');
      dispatch(dispatchAddCompanyInvestor(result.companyInvestor));
      if (result.latestFinancialReport) {
        dispatch(dispatchAddFinancialReport(result.latestFinancialReport));
      }
      toggleSaving(false);
      if (onSave) onSave(result.companyInvestor);
      onClose();
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error saving investor');
    }
  };

  const handleSearchCompanies = async (search: string) => {
    try {
      const results = await searchCompanies(search);
      return results.map(buildCompanyOption);
    } catch (err) {
      onApiError(err, 'Error fetching companies');
      throw err;
    }
  };

  const handleSearchInvestors = async (search: string) => {
    try {
      const results = await searchInvestors(search);
      return results.map(buildInvestorOption);
    } catch (err) {
      onApiError(err, 'Error fetching investors');
      throw err;
    }
  };

  return (
    <Dialog className={classes.dialog} onClose={onClose}>
      <DialogTitle
        className={classes.dialogTitle}
        title="New company investor"
        subtitle="Add an investor to a company"
        onClose={onClose}
      />
      <DialogBody className={classes.body}>
        <SearchableDropdown
          disabled={isSaving}
          error={invalidFields.includes('selectedCompany')}
          className={classes.dropdown}
          name="selectedCompany"
          label="Company"
          value={selectedCompany}
          initialOptions={[]}
          onSearch={handleSearchCompanies}
          onSelect={(update) => setSelectedCompany(update.selectedCompany)}
        />
        <SearchableDropdown
          disabled={isSaving}
          error={invalidFields.includes('selectedInvestor')}
          className={classes.dropdown}
          name="selectedInvestor"
          label="Investor"
          value={selectedInvestor}
          initialOptions={[]}
          onSearch={handleSearchInvestors}
          onSelect={(update) => setSelectedInvestor(update.selectedInvestor)}
        />
      </DialogBody>
      <DefaultDialogActions
        disabled={isSaving || !selectedInvestor || !selectedCompany}
        pending={isSaving}
        confirmLabel="Save"
        pendingLabel="Saving..."
        onConfirm={handleSave}
        onCancel={() => onClose()}
      />
    </Dialog>
  );
}
